import React, { useEffect, useRef } from "react";
import { useI18n } from "../../i18n";
import CompleteSaleButton from "./CompleteSaleButton";
import CustomerScan from "./CustomerScan";
import DiscountRow from "./DiscountRow";
import PaymentSection from "./PaymentSection";
import Sheet from "./Sheet";
import TicketActions from "./TicketActions";
import TicketLines from "./TicketLines";
import TicketTotals from "./TicketTotals";
import { formatMoney } from "../utils/money";
import { hasTab, paymentState } from "../utils/payment";

/**
 * Phone version of the ticket: one sheet that scrolls, holding the basket, the
 * customer, the discount and the payment.
 *
 * It used to be two sheets — review, then pay — which cost a tap each way and
 * hid the basket while the money was being taken. Now Charge opens this sheet
 * scrolled to the payment, Complete is pinned to the footer, and the basket is
 * a thumb-flick up: an exact-cash sale is still Charge → Complete.
 */
const TicketSheet = ({
  open,
  focus,
  onClose,
  ticket,
  currency,
  payment,
  customer,
  onCustomerPicked,
  onCustomerCleared,
  canExtendCredit,
  error,
  onHold,
  onClear,
}) => {
  const { t } = useI18n();
  const paymentRef = useRef(null);
  const { isShort, isCreditBlocked } = paymentState(
    payment.method,
    payment.tendered,
    ticket.totals.total,
    hasTab(customer, payment.creditTab),
  );

  // Opened from Charge: land on the payment, not the top of a long basket
  useEffect(() => {
    if (open && focus === "pay") {
      paymentRef.current?.scrollIntoView({ block: "start" });
    }
  }, [open, focus]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("pos.ticket.title")}
      subtitle={`${t("common.items", {
        count: ticket.totals.itemCount,
      })} · ${formatMoney(ticket.totals.total, currency)}`}
      actions={
        <TicketActions
          disabled={ticket.isEmpty}
          onHold={onHold}
          onClear={onClear}
        />
      }
      footer={
        <CompleteSaleButton
          total={ticket.totals.total}
          currency={currency}
          submitting={payment.submitting}
          disabled={
            ticket.isEmpty || payment.submitting || isShort || isCreditBlocked
          }
          onClick={payment.onComplete}
        />
      }
    >
      <TicketLines
        lines={ticket.lines}
        currency={currency}
        onSetQuantity={ticket.setQuantity}
        onRemove={ticket.removeProduct}
      />

      {!ticket.isEmpty && (
        <>
          <CustomerScan
            customer={customer}
            onPicked={onCustomerPicked}
            onCleared={onCustomerCleared}
            currency={currency}
          />
          <DiscountRow
            discount={ticket.discount}
            discountAmount={ticket.totals.discountAmount}
            currency={currency}
            onChange={ticket.setDiscount}
          />
          {/* Compact: the pinned Complete button already carries the total */}
          <TicketTotals totals={ticket.totals} currency={currency} compact />

          <div ref={paymentRef} className="border-t border-slate-800 scroll-mt-2">
            {/* A failed sale has to be said here — the sheet covers the
                register, where the error would otherwise be shown */}
            {error && (
              <p className="mx-4 mt-4 rounded-xl bg-red-500/15 border border-red-500/40 px-3 py-2 text-sm font-semibold text-red-200">
                ⚠️ {error}
              </p>
            )}
            <PaymentSection
              totals={ticket.totals}
              currency={currency}
              disabled={ticket.isEmpty}
              showCompleteButton={false}
              collapsibleKeypad
              customer={customer}
              canExtendCredit={canExtendCredit}
              {...payment}
            />
          </div>
        </>
      )}
    </Sheet>
  );
};

export default TicketSheet;
